import React, { Component } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import Helmet from 'react-helmet';
import StepHeader from '../../includes/StepHeader';
import { RequiresAuthenticatedUser } from '../RequiresAuthenticatedUser';
import ManagePaymentsStore from '../../stores/ManagePaymentsStore';
import ActivePaymentList from './activepayments/ActivePaymentList';
import ArchivePaymentList from './archievepayments/ArchivePaymentList';
import AccountsStore from '../../stores/AccountsStore';
import SODDDifferenceModal from './SODDDifferenceModal';
import ErrorHandler from '../../entities/ErrorHandler';
import StringConstants from '../../constants/StringConstants';
import AnalyticsUtil from '../../utils/AnalyticsUtil';
import config from '../../config/index';
import InternetBankingActions from '../../actions/InternetBankingActions';
import ManagePaymentActionCreator from '../../actions/ManagePaymentActionCreator';
import {
  GenericChatComponent,
  getChatContent,
} from '../common/GenericChatComponent';
import { sayLandingMessage } from '../../actions/AccessibilityActionCreator';
import UserManagementStore from '../../stores/UserManagementStore';

class ManagePaymentNew extends Component {
  constructor(props) {
    super(props);

    this.onManagePaymentStoreChange =
      this.onManagePaymentStoreChange.bind(this);
    this.goBack = this.goBack.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.reload = this.reload.bind(this);
    this.toggleDifferenceModal = this.toggleDifferenceModal.bind(this);

    this.state = {
      isLoading: true,
      urlUpdate: false,
      key: 1,
      showDifferenceModal: false,
      showErrorComponent: false,
      showClickToReload: false,
      errorObject: {},
    };
  }

  componentDidMount() {
    /**
     * reset the `ManagePaymentsStore`'s internal DD and SO request iterator count.
     * If a previous page view results in failed DD/SO requests, the store will not emit any change event,
     * leaving us stuck in a permanent "isLoading" state.
     */
    ManagePaymentsStore.reset();
    /**
     * Start listening for store changes once the component successfully mounted in the DOM.
     */
    ManagePaymentsStore.addChangeListener(this.onManagePaymentStoreChange);

    AnalyticsUtil.analyticAction(
      config.analytics.analytics_experience_view,
      config.analytics.analytics_event_method_appeared,
      'PageLoaded'
    );
    sayLandingMessage(StringConstants.managePaymentHeader);

    document.body.classList.toggle('bow', true);

    /**
     * Call the payment list service and show the loader on table component
     * The act of getting the payment lists from the service will trigger a subsequent
     * "Change" event in the `ManagePaymentsStore`, which we have registered a listener for above.
     * This will result in `onManagePaymentStoreChange` being called and the list being
     * updated with the latest data.
     *
     * This rectifies issues reported by customers, where adding a new recurring payment (e.g a Standing Order)
     * would not be reflected in the UI, as previously this screen only fetched data if existing store data was `null`.
     * We now check for update payment list data when this component mounts.
     * @see {@link[PB-8681]{https://enterprisevm.atlassian.net/browse/PB-8681}}
     */
    ManagePaymentActionCreator.getPaymentList();
  }

  componentWillUnmount() {
    ManagePaymentsStore.removeChangeListener(this.onManagePaymentStoreChange);
    document.body.classList.toggle('bow', false);

    if (!this.state.urlUpdate) {
      if (ManagePaymentsStore.getBackUrl() === StringConstants.payeeTaskUrl) {
        ManagePaymentsStore.resetMoveMoneyUrl();
      }
    }
  }

  onManagePaymentStoreChange() {
    const activePaymentList = ManagePaymentsStore.getPaymentList();

    if (activePaymentList instanceof ErrorHandler) {
      this.setState({
        errorObject: activePaymentList,
        activePaymentList: [],
        archivePaymentList: [],
        showClickToReload: true,
        isLoading: true,
      });
    } else {
      this.setState({
        errorObject: {},
        activePaymentList,
        archivePaymentList: ManagePaymentsStore.getArchivePayments(),
        isLoading: false,
      });
    }
  }

  goBack() {
    this.setState({
      urlUpdate: true,
    });

    const backUrl = ManagePaymentsStore.getBackUrl();

    InternetBankingActions.navigateToWebTask(
      backUrl === StringConstants.payeeTaskUrl ? 'WEB-OPEN-MOVEMONEY' : backUrl
    );
  }

  handleSelect(key) {
    this.setState({ key });
  }

  reload() {
    this.setState({
      showClickToReload: false,
    });

    /**
     * Reset the API successful call counts vs error counts. Otherwise, any change in the
     * `ManagePaymentsStore` won't be emitted and therefore `onManagePaymentStoreChange`
     * won't be called after any network failures.
     */
    ManagePaymentsStore.reset();
    ManagePaymentActionCreator.getPaymentList();
  }

  toggleDifferenceModal() {
    this.setState((state) => {
      return { showDifferenceModal: !state.showDifferenceModal };
    });
  }

  render() {
    return (
      <div>
        <Helmet title="Your scheduled payments" />
        <div className="bow page">
          <div
            className="container managepayment-container"
            id="site-main"
            role="main"
            tabIndex="-1"
          >
            <div className="row">
              <div className="col-xs1-24 col-md1-18">
                <StepHeader
                  showheaderCrumbs
                  onClick={this.goBack}
                  headerCrumbsMessage="Back"
                  headerTitle={StringConstants.managePaymentHeader}
                />
                <div className="noprint">
                  <p>{StringConstants.managePaymentSubheader}</p>
                  <p>
                    <a
                      href="javascript:void(0);"
                      onClick={this.toggleDifferenceModal}
                    >
                      {StringConstants.aboutDDSO}
                    </a>
                  </p>
                </div>
                {this.state.showDifferenceModal ? (
                  <SODDDifferenceModal
                    content={this.props.content}
                    closeModal={this.showDifferenceModal}
                  />
                ) : null}
                <Tabs
                  activeKey={this.state.key}
                  onSelect={this.handleSelect}
                  id="manage-payment-tab"
                  className="c-scroll-tabs"
                >
                  <Tab eventKey={1} title="Active">
                    <ActivePaymentList
                      {...this.props}
                      activeList={this.state.activePaymentList}
                      isCreditCardUser={AccountsStore.isCreditCardUser()}
                      hasError={this.state.showClickToReload}
                      clickToReload={this.reload}
                      isLoading={this.state.isLoading}
                      errorObject={this.state.errorObject}
                    />
                  </Tab>
                  <Tab eventKey={2} title="Archived">
                    <ArchivePaymentList
                      {...this.props}
                      cancelledList={this.state.archivePaymentList}
                      isCreditCardUser={AccountsStore.isCreditCardUser()}
                      isLoading={this.state.isLoading}
                      clickToReload={this.reload}
                      hasError={this.state.showClickToReload}
                      errorObject={this.state.errorObject}
                    />
                  </Tab>
                </Tabs>
              </div>
              {UserManagementStore.isBusinessUser() && (
                <div className="col-sm1-24 col-md1-6 scheduled-payments__chat">
                  <GenericChatComponent
                    content={getChatContent(this.props.content)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RequiresAuthenticatedUser(ManagePaymentNew);
